import MDBox from "components/MDBox";
import MDProgress from "components/MDProgress";
import MDTypography from "components/MDTypography";
import { ImportJobState, ImportRowRejectionRecord } from "api/features/import/ops";
import {
  getImportJobDisplayStatus,
  getImportStepProgressColor,
  mapDisplayStatusToProgressColor,
} from "./hooks/importJobDisplay";

type ImportProgressPanelProps = {
  jobId: string | null;
  job?: ImportJobState;
  isTimeoutFailure: boolean;
  totalRejected: number;
  persistedRejections: ImportRowRejectionRecord[];
  persistedRejectionTotal: number;
  rejectionsLoading: boolean;
};

function ImportProgressPanel({
  jobId,
  job,
  isTimeoutFailure,
  totalRejected,
  persistedRejections,
  persistedRejectionTotal,
  rejectionsLoading,
}: ImportProgressPanelProps): JSX.Element {
  const logs = job?.logs ?? [];
  const formatRows = (value?: number) => (typeof value === "number" ? value.toLocaleString() : "-");
  const displayStatus = getImportJobDisplayStatus(job, totalRejected);
  const overallProgressColor = mapDisplayStatusToProgressColor(
    isTimeoutFailure ? "failed" : displayStatus
  );

  if (!jobId) {
    return (
      <MDTypography variant="body2" color="text">
        No import started yet.
      </MDTypography>
    );
  }

  return (
    <>
      {isTimeoutFailure && (
        <MDBox
          p={1.5}
          mb={1}
          borderRadius="md"
          border="1px solid"
          sx={{
            borderColor: "error.main",
            backgroundColor: "rgba(244, 67, 54, 0.08)",
          }}
        >
          <MDTypography variant="button" color="error" fontWeight="bold">
            Import interrompu (timeout)
          </MDTypography>
          <MDTypography variant="caption" display="block" color="text" mt={0.5}>
            Le delai maximal a ete depasse. Le traitement backend a ete annule.
          </MDTypography>
        </MDBox>
      )}

      <MDBox
        p={1.5}
        borderRadius="md"
        border="1px solid"
        sx={{ borderColor: "rgba(0, 0, 0, 0.12)" }}
      >
        <MDTypography variant="button" color="text" fontWeight="bold">
          Overall Progress
          {job?.status ? ` (${displayStatus === "failed" ? "failed" : job.status})` : ""}
        </MDTypography>
        <MDTypography variant="h4" color="dark" fontWeight="bold" lineHeight={1.2}>
          {job?.progressPct ?? 0}%
        </MDTypography>
        {displayStatus === "failed" && job?.error && (
          <MDTypography variant="caption" display="block" color="error" mt={0.5}>
            {job.error}
          </MDTypography>
        )}
        {totalRejected > 0 && displayStatus !== "failed" && (
          <MDTypography variant="caption" display="block" color="warning" mt={0.5}>
            {totalRejected.toLocaleString()} rejected row(s) — import will fail; production will not
            be updated.
          </MDTypography>
        )}
        <MDBox mt={0.75}>
          <MDProgress value={job?.progressPct ?? 0} color={overallProgressColor} />
        </MDBox>
      </MDBox>

      <MDBox mt={1}>
        {(job?.steps ?? []).map((step) => (
          <MDBox key={step.key} mt={0.75}>
            <MDTypography variant="caption" display="block" color="text">
              {step.label}: {step.status} - {step.progressPct}%
              {typeof step.totalRows === "number"
                ? ` (${formatRows(step.processedRows)}/${formatRows(step.totalRows)} rows)`
                : ` (${formatRows(step.processedRows)} rows processed)`}
              {` | rejected: ${step.rejectedCount}`}
            </MDTypography>
            <MDProgress
              value={step.progressPct}
              color={getImportStepProgressColor(step, { isTimeoutFailure })}
            />
          </MDBox>
        ))}
      </MDBox>

      <MDBox mt={1}>
        {totalRejected > 0 && (
          <MDTypography variant="caption" display="block" color="text" mb={0.5}>
            Rejected rows: {totalRejected.toLocaleString()}
            {persistedRejectionTotal > persistedRejections.length
              ? ` (showing ${
                  persistedRejections.length
                } of ${persistedRejectionTotal.toLocaleString()} stored)`
              : ""}
          </MDTypography>
        )}
        {persistedRejections.length > 0 &&
          persistedRejections.map((entry) => (
            <MDTypography key={`db-${entry.id}`} variant="caption" display="block" color="error">
              [{entry.stepKey}] line {entry.lineNumber} - {entry.errorMessage}
            </MDTypography>
          ))}
        {persistedRejections.length === 0 &&
          logs.slice(-20).map((entry) => (
            <MDTypography key={`mem-${entry.id}`} variant="caption" display="block" color="error">
              [{entry.stepKey}] line {entry.line} - {entry.errorMessage}
            </MDTypography>
          ))}
        {persistedRejections.length === 0 && logs.length === 0 && totalRejected === 0 && (
          <MDTypography variant="caption" color="text">
            No rejected rows so far.
          </MDTypography>
        )}
        {rejectionsLoading && totalRejected > 0 && (
          <MDTypography variant="caption" color="text">
            Loading rejection details...
          </MDTypography>
        )}
      </MDBox>
    </>
  );
}

export default ImportProgressPanel;
